import Conversation from './models/Conversation'
import { MessagingAPI } from './API'
import { collection } from 'infra/collection'
import { PrimeMemberByPublicId } from 'iam/MemberStore'
import { ConversationDTO, MessageEventsDTO, MessageDTO } from './dtos/messages'
import {
  EncodedCursor,
  CursorPaged,
  PrimeProps,
  Id,
  PageActivity,
} from 'types/definitions'
import { runInAction, action, computed, makeObservable } from 'mobx'
import { Observable, Subject, merge, firstValueFrom } from 'rxjs'
import User from 'iam/models/User'
import groupBy from 'lodash/groupBy'
import {
  switchMap,
  share,
  concatMap,
  filter,
  mergeMap,
  map,
  first,
  concatAll,
} from 'rxjs/operators'
import Message from './models/Message'
import {
  shareBehavior,
  flowToggle,
  filterNonNil,
  batchElements,
} from 'utils/rx-util'
import Workspace from 'iam/models/Workspace'
import { PrimeUser } from 'iam/UserStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { MessageDeliveryStatus } from './models/MessageStatus'
import { retry } from 'utils/retry-util'
import memoizee from 'memoizee'
import Team from 'iam/models/Team'
import { MessageType } from './models/MessageType'
import { GetCurrentUser } from 'iam/SessionStore'
import { PrimeProgression } from 'jobs/progression/JobProgressionStore'

/**
 * Prime conversation callback.
 */
export type PrimeConversation = (
  props: PrimeProps<ConversationDTO, 'id'>,
) => Conversation | undefined

/**
 * Conversation store.
 */
export default class ConversationStore {
  /**
   * When marking messages as read, they go through here.
   */
  private readonly markAsReadRequests = new Subject<Message>()

  /**
   * Used for batch-requesting online status for users. Element string represents
   * the user ID.
   *
   * @private
   */
  private readonly fetchOnlineStatusRequests = new Subject<string>()

  /**
   * Pipeline for fetching the online status of multiple users in a batch.
   *
   * @private
   */
  private readonly fetchOnlineStatus$ = this.fetchOnlineStatusRequests.pipe(
    // Buffer for 100ms, max 500 elements
    batchElements(100, 500),
    // Perform the actual fetch
    concatMap((ids) => this.api.conversations.isOnline(ids)),
    // Flatten the batch results so we get a stream of individual results.
    concatAll(),
    // Only keep a single subscription to the pipeline ever
    share(),
  )

  /**
   * Conversations.
   */
  conversations = collection<ConversationDTO, Conversation>({
    create: () =>
      new Conversation(
        this.primeWorkspace,
        this.primeMember,
        this.primeProgressionStep,
        this.getCurrentUser,
      ),
  })

  /**
   * Observable for new messages.
   */
  newMessages$: Observable<Message>

  /**
   * Message events.
   */
  events$: Observable<MessageEvent>

  constructor(
    private api: MessagingAPI,
    private primeWorkspace: PrimeWorkspace,
    private primeMember: PrimeMemberByPublicId,
    private primeUser: PrimeUser,
    private primeProgressionStep: PrimeProgression,
    private getCurrentUser: GetCurrentUser,
    currentUser$: Observable<User>,
    pageActivity$: Observable<PageActivity>,
  ) {
    makeObservable(this)
    this.fetchMessages = this.fetchMessages.bind(this)
    this.sendMessage = this.sendMessage.bind(this)
    this.markAsRead = this.markAsRead.bind(this)
    this.toggleMute = this.toggleMute.bind(this)
    this.muteParticipant = this.muteParticipant.bind(this)
    this.unMuteParticipant = this.unMuteParticipant.bind(this)
    this.fetchConversation = memoizee(this.fetchConversation.bind(this), {
      promise: true,
      length: 1,
    })

    // Subscribe to Conversation events.
    this.events$ = this.api.events$.pipe(
      concatMap((e) => this._handleEvent(e)),
      filterNonNil(),
      share(),
    )

    this.newMessages$ = this.events$.pipe(
      filter((e): e is MessageCreated => e.type === 'MessageCreated'),
      map((e) => e.message),
      share(),
    )

    merge(this.events$, this.newMessages$, this.fetchOnlineStatus$).subscribe()

    // Fetch unread counts on login.
    currentUser$.pipe(switchMap(() => this.fetchUnreadCounts())).subscribe()

    // Observe page activity.
    const pageIsActive$ = pageActivity$.pipe(
      shareBehavior<PageActivity>(PageActivity.Active),
      map((v) => v === PageActivity.Active),
    )

    // "Mark as Read" pipeline
    currentUser$
      .pipe(
        switchMap((currentUser) =>
          this.markAsReadRequests.pipe(
            // We only care about statuses that are not already read
            // by the current user.
            filter((m) => {
              const status = m.statusFor(currentUser) || false
              return (
                status && status.deliveryStatus !== MessageDeliveryStatus.Read
              )
            }),
            // While the page is active, open the flood gates.
            flowToggle(pageIsActive$),
            // Buffer requests so we can batch them.
            batchElements(100, 100),
            // Perform mark as read operation.
            mergeMap((batch) =>
              retry((onErrRetry) =>
                this.batchMarkAsRead(currentUser, batch).catch(onErrRetry),
              ),
            ),
          ),
        ),
      )
      .subscribe()
  }

  /**
   * The total unread count.
   */
  @computed
  get totalUnreadCount() {
    return this.conversations.items.reduce(
      (accum, convo) => convo.unreadCount + accum,
      0,
    )
  }

  /**
   * Prime conversation.
   */
  primeConversation: PrimeConversation = (props) =>
    this.conversations.set(props)

  /**
   * Gets a participant status change stream for a particular user.
   * @param user
   */
  getParticipantStatusStreamForUser(user: User) {
    return this.events$.pipe(
      filter((e) => e.type === 'ParticipantStatusChanged' && e.user === user),
    )
  }

  /**
   * Gets a participant status change stream for a particular user.
   * @param user
   */
  getParticipantStatusStreamForSearchableUser(userId: Id) {
    return this.events$.pipe(
      filter(
        (e) =>
          e.type === 'ParticipantStatusChanged' && e.user.publicId === userId,
      ),
    )
  }

  /**
   * Get conversations for a workspace.
   */
  forWorkspace = (workspace: Workspace) => {
    return this.conversations.filter((c) => c.workspace === workspace)
  }

  /**
   * Fetches a single conversation.
   *
   * @param conversationId
   */
  async fetchConversation(conversationId: Id) {
    return this.api.conversations
      .getConversationById(Id.assert(conversationId))
      .then((data) => this.conversations.set(data))
  }

  /**
   * Sends a message.
   *
   * @param conversation the conversation
   * @param params the parameters
   */
  async sendMessage(conversation: Conversation, params: SendMessageParams) {
    let result: MessageDTO
    switch (params.type) {
      case MessageType.Message:
        result = await this.api.conversations.createMessage(
          Id.assert(conversation.id),
          params.message,
        )
        break
      case MessageType.Note:
        result =
          params.team === Team.none
            ? await this.api.conversations.createNote(
                Id.assert(conversation.id),
                params.message,
              )
            : await this.api.conversations.createTeamNote(
                Id.assert(conversation.id),
                Id.assert(params.team.id),
                params.message,
              )
        break
      default:
        throw new Error(`Unable to send a message of type ${params.type}`)
    }

    conversation.messages.set(result)
  }

  /**
   * Sends an admin message.
   *
   * @param conversation the conversation
   * @param params the parameters
   */
  async sendAdminMessage(
    conversation: Conversation,
    params: SendMessageParams,
  ) {
    let result: MessageDTO
    switch (params.type) {
      case MessageType.Message:
        result = await this.api.conversations.createAdminMessage(
          Id.assert(conversation.id),
          params.message,
        )
        break
      case MessageType.Note:
        result = await this.api.conversations.createAdminNote(
          Id.assert(conversation.id),
          params.message,
        )
        break
      default:
        throw new Error(`Unable to send a message of type ${params.type}`)
    }

    conversation.messages.set(result)
  }

  /**
   * Updates a message.
   *
   * @param conversation the conversation
   * @param messageId the message id
   * @param text the text
   */
  async updateMessage(conversation: Conversation, messageId: Id, text: string) {
    const result = await this.api.conversations.updateMessage(
      Id.assert(messageId),
      text,
    )

    conversation.messages.set(result)
  }

  /**
   * Fetches unread counts for a user.
   */
  async fetchUnreadCounts() {
    const result = await this.api.conversations.getUnreadCounts()
    runInAction(() => {
      result.forEach((item) => {
        const conversation = this.conversations.set({
          id: item.conversation_id,
          workspace_id: item.workspace_id,
          // We use the dateModified on the Conversation
          // as a way of determining job activity.
          date_modified: item.last_message.date_modified,
        })
        conversation.resetUnreadCount(item.unread_count)
      })
    })
  }

  /**
   * Fetches messages for a conversation.
   */
  async fetchMessages({
    conversationId,
    limit,
    teamId,
    after,
  }: FetchMessageForConversationQuery): Promise<CursorPaged<Message>> {
    const conversation = await this.fetchConversation(conversationId)
    const page = await this.api.conversations.getMessages(conversationId, {
      limit,
      after,
      conversation_id: conversationId,
      team_id: teamId || undefined,
      include_status: true,
    })

    return CursorPaged.map(page, conversation.messages.set)
  }

  /**
   * Fetches the total amount of human messages in a conversation.
   */
  async fetchHumanMessagesTotalCount(
    conversationId?: string,
    workspaceId?: string,
  ): Promise<void> {
    if (conversationId === undefined || workspaceId === undefined) {
      return
    }
    const conversation = this.conversations.find((c) => c.id === conversationId)
    if (conversation === undefined) {
      return
    }

    const humanMessages = await this.api.conversations.getHumanMessages(
      conversationId,
      workspaceId,
    )

    conversation.setHumanMessagesCount(humanMessages.length)
  }

  /**
   * Fetches the online status for a user.
   *
   * @param user
   */
  async fetchOnlineStatusForUser(user: User): Promise<boolean> {
    const promise = firstValueFrom(
      this.fetchOnlineStatus$.pipe(
        first((r) => r.user_public_id === user.publicId),
      ),
    )
    this.fetchOnlineStatusRequests.next(Id.assert(user.publicId))
    const result = await promise
    return result.online
  }

  /**
   * Fetches the online status for a user.
   *
   * @param user
   */
  async fetchOnlineStatusForSearchableUser(userId: Id): Promise<boolean> {
    const promise = firstValueFrom(
      this.fetchOnlineStatus$.pipe(first((r) => r.user_public_id === userId)),
    )
    this.fetchOnlineStatusRequests.next(Id.assert(userId))
    const result = await promise
    return result.online
  }

  /**
   * Marks the message as read.
   *
   * @param message
   */
  markAsRead(message: Message) {
    this.markAsReadRequests.next(message)
  }

  async toggleMute(conversation: Conversation) {
    const participant = conversation.participants.get(
      Id.assert(this.getCurrentUser().id),
    )
    if (participant) {
      await (participant.muted
        ? this.unMuteParticipant(conversation)
        : this.muteParticipant(conversation))
      return participant
    }
    return null
  }

  async muteParticipant(conversation: Conversation) {
    if (conversation === Conversation.none) {
      return
    }
    const id = Id.assert(conversation.id)
    const participant = await this.api.conversations.updateParticipant({
      muted: true,
      user_id: Id.assert(this.getCurrentUser().publicId),
      conversation_id: id,
    })
    return conversation.participants.set(participant)
  }

  async unMuteParticipant(conversation: Conversation) {
    if (conversation === Conversation.none) {
      return
    }
    const id = Id.assert(conversation.id)
    const participant = await this.api.conversations.updateParticipant({
      muted: false,
      user_id: Id.assert(this.getCurrentUser().publicId),
      conversation_id: id,
    })
    return conversation.participants.set(participant)
  }

  /**
   * Mass marks as read. Called from the observable setup above.
   *
   * @param currentUser
   * @param batch
   */
  private async batchMarkAsRead(currentUser: User, batch: Message[]) {
    const grouped = groupBy(batch, (x) => x.conversation.workspace.id)
    return Promise.all(
      Object.keys(grouped).map(async (workspaceId) => {
        const msgs = grouped[workspaceId]
        runInAction(() => msgs.forEach((m) => m.markRead(currentUser)))
        await this.api.conversations
          .markRead(
            workspaceId,
            msgs.map((m) => Id.assert(m.id)),
          )
          .catch((err) => {
            console.error('Failed to mark messages as read', err)
          })
      }),
    )
  }

  /**
   * Handles a real-time event and transforms it to a nicer shape.
   *
   * @param event
   */
  @action.bound
  private async _handleEvent(
    event: MessageEventsDTO,
  ): Promise<MessageEvent | undefined> {
    if (!event.type) {
      return
    }
    switch (event.type) {
      case 'MESSAGE_CREATED': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        const message = convo.messages.set(event.payload)
        return { message, type: 'MessageCreated' }
      }
      case 'MESSAGE_UPDATED': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        const message = convo.messages.set(event.payload)
        return { message, type: 'MessageUpdated' }
      }
      case 'MESSAGE_DELETED': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        convo.messages.remove(event.payload.id)
        break
      }
      case 'MESSAGE_DELIVERED': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        const message = convo.messages.get(event.payload.id)
        if (message) {
          message.markDelivered(
            this.primeUser({ public_id: event.payload.user_public_id }),
          )
        }
        break
      }
      case 'MESSAGE_READ': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        const message = convo.messages.get(event.payload.id)
        if (message) {
          message.markRead(
            this.primeUser({ public_id: event.payload.user_public_id }),
          )
        }
        break
      }
      case 'PARTICIPANT_ADDED': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        convo.participants.set([
          {
            conversation_id: event.payload.conversation_id,
            user_id: event.payload.user_id,
            workspace_id: event.payload.workspace_id,
          },
        ])
        break
      }
      case 'PARTICIPANT_REMOVED': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        convo.participants.remove(event.payload.user_id)
        break
      }
      case 'PARTICIPANT_STATUS_CHANGED': {
        const user = this.primeUser({
          public_id: event.payload.user_public_id,
        })
        return {
          user,
          type: 'ParticipantStatusChanged',
        }
      }
    }
  }
}

export interface FetchMessageForConversationQuery {
  conversationId: string
  limit?: number
  teamId?: string | null
  after?: EncodedCursor
}

/**
 * Message event
 */
export type MessageEvent =
  | MessageCreated
  | MessageUpdated
  | ParticipantStatusChanged
  | MessageUpdated

export interface MessageCreated {
  type: 'MessageCreated'
  message: Message
}

export interface MessageUpdated {
  type: 'MessageUpdated'
  message: Message
}

export interface ParticipantStatusChanged {
  type: 'ParticipantStatusChanged'
  user: User
}

export interface SendMessageParams {
  message: string
  team: Team
  type: MessageType
}
